import { Button, ColorInput, Group, Loader, NumberInput, Stack, Text, TextInput } from "@mantine/core";
import { IconCheck } from "@tabler/icons-react";
import type * as React from "react";
import { env } from "../../env";
import { MicrosoftLogo } from "../../ui/brandLogos";
import { ColumnHeading, InlineField } from "../../ui/fields";
import { PANEL_PALETTES, PANEL_SPACE } from "../../ui/tokens";
import { LabelsFields } from "../standards/LabelsFields";
import type { StandardsDraft } from "../standardsModel";
import type { SignInWatch } from "./useSignInWatch";

type DraftProps = { draft: StandardsDraft; patch: (partial: Partial<StandardsDraft>) => void };

/** The line under a step's title (`text/secondary`). */
export function StepNote({ children }: { children: React.ReactNode }) {
  return (
    <Text c="dimmed" lh={1.25}>
      {children}
    </Text>
  );
}

/** Step 1 (frame 15185:5272): the firm's name. */
export function NameStep({ name, onName, onSubmit }: { name: string; onName: (v: string) => void; onSubmit: () => void }) {
  return (
    <TextInput
      label="What is your organization called?"
      placeholder="ACME Corp"
      maxLength={80}
      value={name}
      onChange={(e) => onName(e.currentTarget.value)}
      onKeyDown={(e) => e.key === "Enter" && onSubmit()}
      data-autofocus
    />
  );
}

/** Step 2 (frame 15185:5392): the six series accents. */
export function ColorsStep({ draft, patch }: DraftProps) {
  return (
    <Stack gap={PANEL_SPACE.field}>
      {draft.seriesColors.map((c, i) => (
        <InlineField key={i} label={`Accent ${i + 1}`}>
          {(id) => (
            <ColorInput
              id={id}
              format="hex"
              value={c}
              withEyeDropper={false}
              onChange={(v) => patch({ seriesColors: draft.seriesColors.map((old, j) => (j === i ? v : old)) })}
            />
          )}
        </InlineField>
      ))}
    </Stack>
  );
}

function FontRows({ heading, fontKey, sizeKey, draft, patch }: DraftProps & { heading: string; fontKey: "headingFont" | "bodyFont"; sizeKey: "headingSize" | "bodySize" }) {
  return (
    <Stack gap={PANEL_SPACE.field}>
      <ColumnHeading>{heading}</ColumnHeading>
      <InlineField label="Font Family">
        {(id) => <TextInput id={id} maxLength={80} value={draft[fontKey]} onChange={(e) => patch({ [fontKey]: e.currentTarget.value })} />}
      </InlineField>
      <InlineField label="Font Size">
        {(id) => (
          <NumberInput
            id={id}
            suffix="pt"
            min={6}
            max={96}
            allowDecimal={false}
            allowNegative={false}
            value={draft[sizeKey]}
            onChange={(v) => patch({ [sizeKey]: typeof v === "number" ? v : "" } as Partial<StandardsDraft>)}
          />
        )}
      </InlineField>
    </Stack>
  );
}

/** Step 3 (frame 15185:5535): heading and body fonts and sizes. The two groups sit 30px
 * apart: a gap measured from an input's border reads tighter than the same gap under a line
 * of text, so this one matches the subtitle's gap to the eye (Dan 2026-10-02). */
export function FontsStep(props: DraftProps) {
  return (
    <Stack gap={PANEL_SPACE.page + PANEL_SPACE.field}>
      <FontRows heading="Slide Headers" fontKey="headingFont" sizeKey="headingSize" {...props} />
      <FontRows heading="Body & Chart Text" fontKey="bodyFont" sizeKey="bodySize" {...props} />
    </Stack>
  );
}

/** Step 4 (frame 15185:5714): the label standards, labels semibold (onboarding only). */
export function LabelsStep(props: DraftProps) {
  return (
    <Stack gap={PANEL_SPACE.field}>
      <LabelsFields {...props} strongLabels />
    </Stack>
  );
}

/** One line of the sign-in watch: an icon, then the words, centered. */
function WatchLine({ icon, color, children }: { icon: React.ReactNode; color?: string; children: React.ReactNode }) {
  return (
    <Group gap={5} justify="center" wrap="nowrap" role="status">
      {icon}
      <Text c={color ?? "dimmed"} lh={1.25}>
        {children}
      </Text>
    </Group>
  );
}

/** Step 5 (frame 15185:5898): AppSource, then a live check for the add-in's sign-in. */
export function SignInStep({ email, watch }: { email: string; watch: SignInWatch }) {
  return (
    <>
      <Button
        component="a"
        href={env.appSourceUrl}
        target="_blank"
        rel="noopener noreferrer"
        color="black"
        fullWidth
        leftSection={<MicrosoftLogo />}
      >
        Get SlideBrain from Microsoft AppSource
      </Button>
      {watch === "signedIn" ? (
        <WatchLine icon={<IconCheck size={18} color={PANEL_PALETTES.green[6]} aria-hidden />} color={PANEL_PALETTES.green[6]}>
          You've signed in successfully!
        </WatchLine>
      ) : (
        <WatchLine icon={<Loader size={14} color="gray" aria-hidden />}>
          {watch === "slow" ? `Still waiting. Make sure you used ${email}` : `Checking for a sign-in from ${email}`}
        </WatchLine>
      )}
    </>
  );
}
